import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. Le relief a sa caméra et sa cible.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'contours',
  family: 'shader',
  danceability: 0.80,
  energyLevel: 0.62,
  bpmMin: 60,
  bpmMax: 180,
  movement: 0.65,
  tonality: 0.65,
  enabled: true
};

// Contours : les courbes de niveau d'un paysage invisible et liquide, en
// lignes néon sur du noir - une carte topographique vivante. Visualiseur
// UNIVERSEL : il glisse sur le curseur d'adapt.js (lib/adapt.js), calme sur
// une ballade, vertigineux sur la techno. Pour tout morceau.
//
// LE CHAMP h(p) : fbm à 3 octaves (2 au tier 0) d'un bruit lissé tiré d'une
// texture 256 x 256, codé sur 16 bits dans deux canaux 8 bits (le filtre
// linéaire interpole les deux canaux, donc leur somme : 16 bits filtrés sur
// le Pi, qui ne filtre pas les flottants). Un domain warp à un niveau,
// amplitude 0,38 x la longueur d'onde de l'octave de base (R14 : <= 0,5).
// L'écoulement : des décalages par octave qui avancent à adapt.motion,
// bouclés modulo la période de la texture (aucune perte de précision au bout
// d'une heure). Plus 5 bosses gaussiennes mobiles (2 grosses : les graves,
// 3 petites : les aigus) et la SOURCE au centre de composition (R19) qui
// respire.
//
// LES LIGNES : v = h x k ; distance à l'entier le plus proche, divisée par
// |grad v| en PIXELS (dFdx, dFdy) : largeur CONSTANTE en pixels dans les
// pentes douces comme raides (R1). Une ligne sur cinq est maîtresse (plus
// épaisse, plus lumineuse, cœur blanc). Là où deux lignes sont à moins de
// 2 px, elles fondent vers leur couverture moyenne : aucun moiré. Couleur :
// dégradé de palette selon l'altitude. Fond noir pur (R3).
//
// ADAPT : k et l'écoulement suivent intensity/motion ; à chaque temps une
// onde part du centre et décale les niveaux, d'amplitude adapt.pulse (nulle
// sur une ballade ou sans grille) ; à défaut de grille, adapt.onset soulève
// la source ; séparation RGB x pulse ; coupes (palette + forme du champ)
// selon adapt.cutPolicy (mesure, phrase, ou fondu à la section). Le drop
// (adapt.drop, seulement sur un morceau qui en a) aplatit le relief en un
// temps puis le relève d'un coup, flash (0,80 : R9 l'autorise).
//
// LES MODES, changés à chaque section : TOPO (vue de dessus), TERRAIN (le
// même champ en relief, fil de fer en perspective, survolé lentement ;
// faces noires, elles masquent les lignes de derrière), POLAR (le champ
// enroulé autour du centre). Tier 0 : 2 octaves, sans TERRAIN.
//
// Debug : ?contourmode=topo|terrain|polar, ?contourk=<f>, ?adaptsim=<profil>.

const MODES = ['topo', 'terrain', 'polar'];
const NOISE_N = 256;
const S0 = 0.035;             // base octave: tex units per height unit
const WARP_AMP = 0.38;        // x the base wavelength (R14 <= 0.5)
const BUMPS = 5;
const SYNTH_BPM = 120;
const PALETTES = [[0x1a2bff, 0x2bd6ff, 0xff2bd6], [0x2b1aff, 0x8a2bff, 0xff7a1f], [0x0a6b5e, 0x2bff9e, 0xffd21f], [0x5e1a8a, 0xff2b5e, 0xfff1d6]];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- the field, shared by the flat pass and the terrain ---------------------------
const FIELD_GLSL = (octaves) => `
  uniform sampler2D uNoise;
  uniform vec2 uOff0;
  uniform vec2 uOff1;
  uniform vec2 uOff2;
  uniform vec2 uOffW;
  uniform vec4 uBump0;
  uniform vec4 uBump1;
  uniform vec4 uBump2;
  uniform vec4 uBump3;
  uniform vec4 uBump4;
  uniform float uCenterAmp;
  uniform float uScale;
  // 16-bit value noise in two 8-bit channels (linear filtering interpolates both)
  float nA(vec2 t) { vec4 c = texture2D(uNoise, t); return (c.r * 65280.0 + c.g * 255.0) / 65535.0; }
  float nB(vec2 t) { vec4 c = texture2D(uNoise, t); return (c.b * 65280.0 + c.a * 255.0) / 65535.0; }
  float bump(vec2 p, vec4 b) { vec2 d = p - b.xy; return b.z * exp(-dot(d, d) / (2.0 * b.w * b.w)); }
  float field(vec2 p) {
    // one level of domain warp, amplitude WARP_AMP x the base wavelength
    vec2 w = vec2(nB(p * ${(S0 * 0.5).toFixed(4)} + uOffW), nB(p * ${(S0 * 0.5).toFixed(4)} + uOffW + vec2(0.371, 0.713))) - 0.5;
    vec2 q = p + w * ${(2.0 * WARP_AMP / (S0 * 16.0)).toFixed(4)};
    float h = nA(q * ${S0.toFixed(4)} + uOff0) * 1.0;
    h += nA(q * ${(S0 * 2.03).toFixed(4)} + uOff1) * 0.5;
    ${octaves > 2 ? `h += nA(q * ${(S0 * 4.11).toFixed(4)} + uOff2) * 0.25;` : ''}
    h = (h - ${octaves > 2 ? '0.875' : '0.75'}) * 1.6 + 0.6;
    h += bump(p, uBump0) + bump(p, uBump1) + bump(p, uBump2) + bump(p, uBump3) + bump(p, uBump4);
    h += uCenterAmp * exp(-dot(p, p) / (2.0 * 0.32 * 0.32));        // the source, at the centre of composition
    return h * uScale;
  }
`;

// the contour lines of a value v (levels at its integers), from its screen
// derivatives: constant px width (R1), master every 5, moire-free
const LINES_GLSL = `
  uniform float uK;
  uniform vec3 uC0;
  uniform vec3 uC1;
  uniform vec3 uC2;
  uniform float uWaveR;
  uniform float uWaveAmp;
  vec3 palette(float t) {
    t = clamp(t, 0.0, 1.0);
    return t < 0.5 ? mix(uC0, uC1, t * 2.0) : mix(uC1, uC2, t * 2.0 - 1.0);
  }
  // returns the line colour for field value h and the level offset sh
  vec3 contour(float h, float sh) {
    float v = h * uK + sh;
    float g = max(length(vec2(dFdx(v), dFdy(v))), 1e-5);          // levels per px
    float d = abs(fract(v + 0.5) - 0.5) / g;                       // px to the nearest level
    float idx = floor(v + 0.5);
    float master = step(mod(idx + 1000.0, 5.0), 0.5);
    float spacing = 1.0 / g;                                       // px between two levels
    // a normal line: 1.6 px; a master: 2.6 px with a 1.2 px white core
    float wN = 0.8, wM = 1.3;
    float lineN = 1.0 - smoothstep(wN - 0.6, wN + 0.6, d);
    // masters: their own spacing is 5 levels
    float vm = v / 5.0;
    float dm = abs(fract(vm + 0.5) - 0.5) / (g / 5.0);
    float lineM = 1.0 - smoothstep(wM - 0.6, wM + 0.6, dm);
    float coreM = 1.0 - smoothstep(0.1, 0.9, dm);
    // under 2 px apart (4 for the masters' own spacing x5) a line becomes its mean coverage
    float fadeN = smoothstep(2.0, 4.0, spacing);
    float fadeM = smoothstep(2.0, 4.0, spacing * 5.0);
    float meanN = min(1.0, 2.0 * wN * g);
    float meanM = min(1.0, 2.0 * wM * g / 5.0);
    float cN = mix(meanN, lineN, fadeN) * (1.0 - master * fadeN);
    float cM = mix(meanM, lineM, fadeM);
    vec3 col = palette(h / 1.3);
    vec3 outc = col * (0.62 * cN + 1.0 * cM) + vec3(1.0) * coreM * fadeM * 0.55;
    return outc;
  }
  float waveShift(vec2 p) {
    float r = length(p);
    return uWaveAmp * exp(-pow((r - uWaveR) / 0.14, 2.0));
  }
`;

// the flat pass (TOPO, POLAR): a clip-space quad
const flatVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const flatFragment = (oct) => `
  precision highp float;
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform float uPolar;
  uniform float uTwist;
  varying vec2 vUv;
  ${FIELD_GLSL(oct)}
  ${LINES_GLSL}
  void main() {
    // height units about the centre of composition (R19)
    vec2 p = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0) - uStageCenter;
    if (uPolar > 0.5) {
      // the field wound around the centre: a rotation that grows with the radius
      float r = length(p), a = uTwist * r;
      p = mat2(cos(a), sin(a), -sin(a), cos(a)) * p;
    }
    float h = field(p * 1.6);
    gl_FragColor = vec4(contour(h, waveShift(p)), 1.0);
  }
`;

// the terrain: the same field as a relief, wireframe of contour lines
const terrainVertex = (oct) => `
  precision highp float;
  uniform float uRelief;
  varying vec2 vXZ;
  ${FIELD_GLSL(oct)}
  void main() {
    vec3 p = position;
    float h = field(p.xz * 1.6);
    p.y = h * uRelief;
    vXZ = p.xz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;
const terrainFragment = (oct) => `
  precision highp float;
  uniform vec2 uFogR;
  varying vec2 vXZ;
  ${FIELD_GLSL(oct)}
  ${LINES_GLSL}
  void main() {
    float h = field(vXZ * 1.6);                                    // the exact field, not the interpolated one
    vec3 col = contour(h, waveShift(vXZ));
    col *= 1.0 - smoothstep(uFogR.x, uFogR.y, length(vXZ));         // black fog at the terrain's rim
    gl_FragColor = vec4(col, 1.0);
  }
`;

// the display: the private target, the cover's safe zone (R19: it follows
// the cover), the comfort gain
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const quadFragment = `
  precision highp float;
  uniform sampler2D uScene;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uScene, vUv).rgb * uGain;
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// a smooth, tileable noise: random values blurred (separable box x3 ~ gauss)
// with wrap-around, two independent fields, each 16-bit in two channels
function noiseTexture() {
  const N = NOISE_N;
  let seed = 1234567;
  const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  const field = () => {
    let a = new Float32Array(N * N);
    for (let i = 0; i < N * N; i++) a[i] = rnd();
    const r = 6;
    for (let pass = 0; pass < 3; pass++) {
      const b = new Float32Array(N * N);
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) { let s = 0; for (let k = -r; k <= r; k++) s += a[y * N + ((x + k + N) % N)]; b[y * N + x] = s / (2 * r + 1); }
      const c = new Float32Array(N * N);
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) { let s = 0; for (let k = -r; k <= r; k++) s += b[((y + k + N) % N) * N + x]; c[y * N + x] = s / (2 * r + 1); }
      a = c;
    }
    // stretch to 0..1
    let mn = Infinity, mx = -Infinity;
    for (const v of a) { mn = Math.min(mn, v); mx = Math.max(mx, v); }
    for (let i = 0; i < a.length; i++) a[i] = (a[i] - mn) / (mx - mn);
    return a;
  };
  const A = field(), B = field();
  const data = new Uint8Array(N * N * 4);
  for (let i = 0; i < N * N; i++) {
    const a = Math.round(A[i] * 65535), b = Math.round(B[i] * 65535);
    data[i * 4] = a >> 8; data[i * 4 + 1] = a & 255; data[i * 4 + 2] = b >> 8; data[i * 4 + 3] = b & 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.needsUpdate = true;
  return t;
}
let NOISE = null;

export class ContoursVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._octaves = this._tier === 0 ? 2 : 3;
    this._modes = this._tier === 0 ? ['topo', 'polar'] : MODES.slice();
    const m = urlParam('contourmode');
    this._forcedMode = this._modes.indexOf(m) >= 0 ? m : null;
    const k = parseFloat(urlParam('contourk'));
    this._forcedK = Number.isFinite(k) && k > 0 ? k : null;
    this._post = new VdjPost({ bloomStrength: 0.30, bloomThreshold: 0.92, vignette: 0.25, grain: 0.008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    if (!NOISE) NOISE = noiseTexture();
    this._adapt = new Adapt();
    this._inner = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(50, 16 / 9, 0.05, 60);
    const u = this._u = {
      uNoise: { value: NOISE }, uOff0: { value: new THREE.Vector2() }, uOff1: { value: new THREE.Vector2() }, uOff2: { value: new THREE.Vector2() }, uOffW: { value: new THREE.Vector2() },
      uBump0: { value: new THREE.Vector4() }, uBump1: { value: new THREE.Vector4() }, uBump2: { value: new THREE.Vector4() }, uBump3: { value: new THREE.Vector4() }, uBump4: { value: new THREE.Vector4() },
      uCenterAmp: { value: 0.2 }, uScale: { value: 1 }, uK: { value: 6 },
      uC0: { value: new THREE.Color() }, uC1: { value: new THREE.Color() }, uC2: { value: new THREE.Color() },
      uWaveR: { value: 9 }, uWaveAmp: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter,
      uPolar: { value: 0 }, uTwist: { value: 1.4 }, uRelief: { value: 1.1 }, uFogR: { value: new THREE.Vector2(4.5, 6.0) }
    };
    // the flat pass
    this._flat = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: u, vertexShader: flatVertex, fragmentShader: flatFragment(this._octaves), depthTest: false, depthWrite: false }));
    this._flat.frustumCulled = false;
    this._inner.add(this._flat);
    // the terrain (tiers 1 and 2)
    if (this._modes.indexOf('terrain') >= 0) {
      const g = new THREE.PlaneGeometry(13, 13, 180, 180).rotateX(-Math.PI / 2);
      this._terrain = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: u, vertexShader: terrainVertex(this._octaves), fragmentShader: terrainFragment(this._octaves), side: THREE.DoubleSide }));
      this._terrain.frustumCulled = false;
      this._inner.add(this._terrain);
    }
    this._rt = new THREE.WebGLRenderTarget(4, 4, { samples: this._tier === 2 ? 4 : 0, type: THREE.UnsignedByteType, colorSpace: THREE.SRGBColorSpace, depthBuffer: true, stencilBuffer: false });
    this._qu = {
      uScene: { value: this._rt.texture }, uGain: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._qu, vertexShader: quadVertex, fragmentShader: quadFragment, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // state
    this._time = 0; this._clock = 0; this._flow = 0;
    this._offs = [new THREE.Vector2(0.13, 0.57), new THREE.Vector2(0.71, 0.29), new THREE.Vector2(0.37, 0.83), new THREE.Vector2(0.05, 0.41)];
    this._seedOff = new THREE.Vector2(0, 0);
    this._beatPeriod = 60 / SYNTH_BPM;
    this._waveT = 9; this._waveAmp = 0;
    this._dropT = 99;
    this._modeIndex = 0; this._mode = this._forcedMode || 'topo';
    this._dip = 0;                        // a mode change with the 'fade' policy: down and up
    this._pendingMode = null;
    this._palIndex = 0; this._vivid = null; this._lastPaletteRef = undefined;
    this._bumpPh = [0.3, 1.9, 3.1, 4.4, 5.6];
    this._camAng = 0;
    this._last = null;
    this._applyColours(0.65);
    this._layoutBumps(null);
  }

  // --- colours: the cover's colours (or a house set), low -> high altitude ----
  _applyColours(sat) {
    let cols;
    const v = this._vivid;
    if (v && v.length >= 3) {
      // the cover's colours by luminance: the darkest low, the brightest high
      const lum = (c) => { const k = new THREE.Color(c); return 0.2126 * k.r + 0.7152 * k.g + 0.0722 * k.b; };
      const s = v.slice(0, 5).sort((a, b) => lum(a) - lum(b));
      const shift = this._palIndex % Math.max(1, s.length - 2);
      cols = [s[shift], s[shift + 1], s[Math.min(s.length - 1, shift + 2)]];
    } else cols = PALETTES[this._palIndex % PALETTES.length];
    vdjColor(cols[0], this._u.uC0.value, sat).multiplyScalar(0.7);
    vdjColor(cols[1], this._u.uC1.value, sat);
    vdjColor(cols[2], this._u.uC2.value, sat);
    this._sat = sat;
  }

  // the moving bumps: 2 big (bass), 3 small (treble), on slow Lissajous paths
  _layoutBumps(a) {
    const bands = a && a.bands;
    const low = bands ? bands.low : 0.3, high = bands ? bands.high : 0.2;
    const U = [this._u.uBump0, this._u.uBump1, this._u.uBump2, this._u.uBump3, this._u.uBump4];
    for (let i = 0; i < BUMPS; i++) {
      const big = i < 2, ph = this._bumpPh[i] + this._flow * (0.6 + 0.17 * i);
      const R = big ? 1.5 : 2.1;
      const x = Math.cos(ph * (big ? 0.7 : 1.1) + i) * R * 0.9, y = Math.sin(ph * (big ? 0.5 : 0.9) + 2 * i) * R * 0.55;
      const amp = big ? 0.30 + 0.45 * low : 0.10 + 0.30 * high;
      U[i].value.set(x, y, amp, big ? 0.55 : 0.22);
    }
  }

  onTrackChange() {}

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors;
      this._vivid = v && v.length ? v.slice() : null;
      this._applyColours(a.saturation);
    }
    if (paused) return;                       // the frame holds
    this._last = a;
    this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;

    // the flow: slow, faster with adapt.motion
    this._flow += dt * 0.045 * a.motion;
    const O = [this._u.uOff0, this._u.uOff1, this._u.uOff2, this._u.uOffW];
    const dirs = [[1, 0.35], [-0.6, 0.8], [0.3, -1], [0.5, 0.5]];
    for (let i = 0; i < 4; i++) {
      const sp = [0.010, 0.016, 0.026, 0.007][i];
      const x = this._offs[i].x + this._seedOff.x * (i + 1) + this._flow * sp * dirs[i][0] / 0.045;
      const y = this._offs[i].y + this._seedOff.y * (i + 1) + this._flow * sp * dirs[i][1] / 0.045;
      O[i].value.set(x - Math.floor(x), y - Math.floor(y));          // wrapped: the texture's period
    }
    // the density of lines: few at rest, tight at full intensity
    this._k = this._forcedK != null ? this._forcedK : 4.5 + 14 * a.intensity;
    // the source breathes; without a grid the attacks lift it
    this._clock += dt;
    const breath = 0.06 * Math.sin(this._clock * 2 * Math.PI / 7);
    const center = 0.22 + breath + 0.35 * a.onset * (1 - Math.min(1, a.pulse * 2));
    // the beat's wave: from the centre, amplitude adapt.pulse (0 on a ballad)
    if (a.beat) { this._waveT = 0; this._waveAmp = 0.55 * a.pulse; }
    this._waveT += dt / this._beatPeriod;
    // the drop: flattened over one beat, then up at once (1.25 settling to 1)
    if (a.drop) {
      this._dropT = 0;
      this._dropFlash = true;
    }
    this._dropT += dt / this._beatPeriod;
    let scale = 1;
    if (this._dropT < 1) scale = 1 - this._dropT;
    else if (this._dropT < 3) scale = 1 + 0.25 * (1 - (this._dropT - 1) / 2);
    if (this._dropFlash && this._dropT >= 1) {
      this._dropFlash = false;
      if (this._comfort.requestFlash(this._time) && director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    }
    // cuts: palette and field shape, by the policy
    const cutNow = (a.cutPolicy === 'bar' && a.bar) || (a.cutPolicy === 'phrase' && a.phrase);
    if (cutNow) this._cut();
    // modes: on each section (a fade dip when the policy is 'fade')
    if (a.section && !this._forcedMode) {
      this._modeIndex = (this._modeIndex + 1) % this._modes.length;
      if (a.cutPolicy === 'fade') { this._pendingMode = this._modes[this._modeIndex]; this._dip = 1e-3; this._cut(); }
      else { this._mode = this._modes[this._modeIndex]; this._cut(); }
    }
    if (this._dip > 0) {
      this._dip += dt / (2 * this._beatPeriod);                    // down over a beat, up over a beat
      if (this._dip >= 0.5 && this._pendingMode) { this._mode = this._pendingMode; this._pendingMode = null; }
      if (this._dip >= 1) this._dip = 0;
    }
    if (Math.abs(a.saturation - this._sat) > 0.01) this._applyColours(a.saturation);
    this._layoutBumps(a);

    const u = this._u;
    u.uK.value = this._k;
    u.uCenterAmp.value = center;
    u.uScale.value = scale;
    u.uWaveR.value = this._waveT * 2.4;
    u.uWaveAmp.value = this._waveT < 1.2 ? this._waveAmp * (1 - this._waveT / 1.2) : 0;
    u.uPolar.value = this._mode === 'polar' ? 1 : 0;
    // RGB split, scaled by the beat's permitted amplitude
    const bp = a.beatPhase != null ? a.beatPhase : 0;
    this._post.update(1 + (vdjBeatChroma(bp, this._beatPeriod) - 1) * a.pulse);
    // comfort: thin lines on black; the cap follows adapt.lumaCap
    const dipGain = this._dip > 0 ? Math.abs(1 - 2 * this._dip) : 1;
    const est = 0.06 + 0.10 * a.intensity + (director && director.flash ? director.flash * 0.6 : 0);
    const g = this._comfort.measure(est, dt);
    this._qu.uGain.value = Math.min(g, a.lumaCap / Math.max(est, 1e-3)) * dipGain;
  }

  _cut() {
    this._palIndex++;
    this._applyColours(this._last ? this._last.saturation : 0.65);
    // a new shape: every octave jumps elsewhere in the noise
    this._seedOff.set((this._palIndex * 0.6180339) % 1, (this._palIndex * 0.4142136) % 1);
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    if (this._rt.width !== this._size.x || this._rt.height !== this._size.y) this._rt.setSize(this._size.x, this._size.y);
    const terrain = this._mode === 'terrain' && this._terrain;
    this._flat.visible = !terrain;
    if (this._terrain) this._terrain.visible = !!terrain;
    if (terrain) {
      // a slow flight around the source, 35 degrees down, at a height clear of the relief
      const cam = this._cam;
      cam.aspect = this._size.x / Math.max(1, this._size.y);
      this._camAng += 0.0016 * (this._last ? this._last.motion : 0.3);
      const R = 5.2, y = 3.4;
      cam.position.set(Math.sin(this._camAng) * R, y, Math.cos(this._camAng) * R);
      cam.up.set(0, 1, 0);
      cam.lookAt(0, 0.3, 0);
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld();
    }
    const prev = renderer.getRenderTarget();
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._cam);
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    const a = this._last || {};
    return {
      t: +this._time.toFixed(2), mode: this._mode, k: +(this._k || 0).toFixed(2), flow: +this._flow.toFixed(4),
      intensity: +(a.intensity || 0).toFixed(3), motion: +(a.motion || 0).toFixed(3), pulse: +(a.pulse || 0).toFixed(3),
      cutPolicy: a.cutPolicy, colorMode: a.colorMode, waveAmp: +this._u.uWaveAmp.value.toFixed(3), scale: +this._u.uScale.value.toFixed(3),
      palIndex: this._palIndex, sim: this._adapt.simulated, gain: +this._qu.uGain.value.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    for (const m of [this._flat, this._terrain]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._rt) this._rt.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}
